import React from "react";
import { ChevronLeft, X, Maximize2, Minimize2, Globe2, Sparkles, CheckCircle2 } from "lucide-react";

function DexKor({
  setView,
  setupDetails,
  setIsOpen,
  isExpanded,
  setIsExpanded,
}) {
  return (
    <div className="w-full flex flex-col h-full bg-white overflow-hidden">
      {/* --- Gleap White Top Header (54px) --- */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
        <button
          onClick={() => setView("DexKorOverview")}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back to collection"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        <div className="font-bold text-slate-900 text-sm tracking-tight text-center truncate px-2 flex-1">
          What is DexKor?
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          {setIsExpanded && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              ) : (
                <Maximize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
              else setView("DexKorOverview");
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* --- Scrollable Article Content --- */}
      <div className="flex-1 overflow-y-auto px-5 pt-0 pb-6 space-y-5 dexkor-scrollbar">
        {/* Sleek Hero Banner (Gleap style) */}
        <div className="relative -mx-5 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white p-6 mb-5">
          <div className="absolute -right-8 -bottom-8 w-48 h-48 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-indigo-400 mb-2">
              <Globe2 className="w-3.5 h-3.5" />
              <span>Enterprise Support Platform</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">
              Your data. Your region.<br />AI-first customer care.
            </h1>
            <p className="text-xs text-slate-400 max-w-md leading-relaxed">
              Designed for reliability, speed, and real-time human & AI collaboration.
            </p>
          </div>
        </div>

        {/* Article Title & Metadata Row */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-snug">
            What is DexKor?
          </h2>
          <div className="flex items-center gap-2.5 mt-2.5 text-xs text-slate-500">
            <div className="w-6 h-6 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center font-bold text-slate-600 text-[10px]">
              D
            </div>
            <span>DexKor Team • 11 hours ago</span>
          </div>
        </div>

        {/* Intro */}
        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal">
          DexKor is an enterprise customer service and ticketing platform built from the ground up for modern digital products. It brings AI automation, live team messaging, and comprehensive ticket tracking together into a lightweight, responsive widget.
        </p>

        {/* Key Section */}
        <div className="space-y-3 pt-2">
          <h3 className="text-sm font-bold text-slate-900">
            What can you do with DexKor?
          </h3>
          <p className="text-xs text-slate-600 leading-relaxed">
            Whether you are running a high-scale consumer application or enterprise B2B suite, DexKor offers the flexibility to cater to every support touchpoint:
          </p>
          <ul className="space-y-2 text-xs text-slate-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span><strong>Omnichannel Tickets:</strong> Collect issues with attachments, priorities, and custom metadata directly from users.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span><strong>Autonomous AI Resolution:</strong> Solve repetitive inquiries with Dexy AI before they ever reach your support queues.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span><strong>Full Screen & Wide Reading:</strong> Expand the widget to focus on deep troubleshooting guides and rich documentation.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default DexKor;
